import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import type { ReviewProcessing } from "@/portals/patient/api/reviewItemsApi";

type StepState = "done" | "active" | "waiting";

/** done = green check, active = red spinner, waiting = grey outline — joined by a thin line like a stepper. */
function Step({ state, icon, label, detail, last = false }: {
  state: StepState; icon: string; label: string; detail?: string; last?: boolean;
}) {
  return (
    <li className="relative flex list-none gap-3 pb-4 last:pb-0" data-step-state={state}>
      {!last && (
        <span
          aria-hidden
          className={cn("absolute left-[11px] top-7 h-[calc(100%-24px)] w-px", state === "done" ? "bg-[#16A34A]/40" : "bg-[#F8C9D2]")}
        />
      )}
      <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
        {state === "done" ? (
          <Icon icon="lucide:circle-check" className="h-6 w-6 text-[#16A34A]" />
        ) : state === "active" ? (
          <Icon icon="lucide:loader-circle" className="h-6 w-6 animate-spin text-[#E4002B]" aria-label="In progress" />
        ) : (
          <Icon icon="lucide:circle" className="h-6 w-6 text-[#D1D5DB]" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 items-center gap-2.5 pt-0.5">
        <Icon
          icon={icon}
          className={cn("h-4 w-4 shrink-0", state === "waiting" ? "text-[#C4C8CF]" : "text-[#0A2947]")}
        />
        <span className={cn("text-sm", state === "waiting" ? "text-[#9CA3AF]" : "font-semibold text-[#0A2947]")}>
          {label}
        </span>
        {detail && (
          <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-[#E4002B] ring-1 ring-[#F8C9D2]">{detail}</span>
        )}
      </div>
    </li>
  );
}

/**
 * Shown on the Human Review Queue while a new upload is still being worked
 * on — so the queue never just looks empty while the worker is busy. Steps:
 * read the documents → match each medicine with RxNorm → compare them with
 * the patient's other records for conflicts. `finished` shows every step done
 * and what came of it, for a moment after the work completes.
 */
export function ReviewProcessingBanner({ processing, finished }: {
  processing?: ReviewProcessing | null;
  finished?: { needsReview: number } | null;
}) {
  const state = finished ? "done" : processing?.state ?? "idle";
  if (state === "idle") return null;
  const step = { reading: 0, checking: 1, comparing: 2, done: 3 }[state];
  const at = (index: number): StepState => (step > index ? "done" : step === index ? "active" : "waiting");
  const checked = processing?.medicationsChecked ?? 0;
  const total = processing?.medicationsTotal ?? 0;
  const files = processing?.uploadsInProgress ?? 0;

  return (
    <section
      aria-label="Checking your new records"
      aria-live="polite"
      className="mb-5 rounded-2xl border border-[#F8C9D2] bg-[#FFF5F7] p-5"
    >
      <h2 className="text-sm font-extrabold text-[#0A2947]">
        {finished ? "Your new records are checked" : "We're checking your new records"}
      </h2>
      <p className="!mb-0 mt-0.5 text-xs text-[#6B7280]">
        {finished
          ? finished.needsReview > 0
            ? `${finished.needsReview} medication${finished.needsReview === 1 ? " needs" : "s need"} your review — see Flagged Records below.`
            : "Nothing new needs your review."
          : "Anything that needs your review will appear here automatically — you can keep using the app meanwhile."}
      </p>
      <ol className="!mb-0 mt-4 border-t border-[#F8C9D2]/70 !pl-0 pt-4">
        <Step
          state={at(0)}
          icon="lucide:file-text"
          label="Reading your documents"
          detail={state === "reading" && files > 1 ? `${files} uploads` : undefined}
        />
        <Step
          state={at(1)}
          icon="lucide:pill"
          label="Identifying your medications"
          detail={state === "checking" && total > 0 ? `${checked} of ${total} checked` : step > 1 && total > 0 ? `${total} checked` : undefined}
        />
        <Step state={at(2)} icon="lucide:git-compare" label="Looking for conflicts with your other records" last />
      </ol>
    </section>
  );
}
